import { styled } from '@linaria/react';
import { Flex } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LegendProps as RechartsLegendProps } from 'recharts';
import { LineChartLegendMarker } from '../../../../components/line-chart/line-chart-legend-marker';
import type { CostVersusOutputMeasures } from '../../../../store/financials-store/financials-store.type';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { COST_VERSUS_OUTPUT_MEASURES } from '../../constants';
import { maybeGetNullLabelForSeries } from '../allocation-of-costs/allocation-of-costs.helpers';
import { getMeasureName } from '../cost-versus-output/cost-versus-output.helpers';

type LegendProps = RechartsLegendProps & {
  hoveredItem: string | null;
  setHoveredItem: (item: string | null) => void;
  minItemWidth?: number; // Minimum width for each legend item
};

const LegendContainer = styled.div<{ minItemWidth: number }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${(props) => props.minItemWidth}px, max-content));
  gap: 12px;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 100%;

  & > * {
    justify-self: center;
  }
`;

function Legend({ hoveredItem, setHoveredItem, minItemWidth = 120, ...props }: LegendProps) {
  const { t } = useTranslation();
  const { payload } = props as any;

  const filteredPayload = useMemo(() => {
    if (!payload) return [];

    // exclude inactive items, trend items, and items with no value
    return (payload as any[]).filter((entry: any) => {
      if ('inactive' in entry && entry.inactive) return false;

      const itemId: string | undefined = entry.id || entry.value;
      if (!itemId) return false;

      if (entry.value && typeof entry.value === 'string' && entry.value.endsWith('_trend')) {
        return false;
      }

      return true;
    });
  }, [payload]);

  if (!filteredPayload || filteredPayload.length === 0) {
    return null;
  }

  return (
    <LegendContainer minItemWidth={minItemWidth}>
      {filteredPayload.map((entry: any, index: number) => {
        const color = entry.color;
        const itemId = entry.id || entry.value || `item-${index}`;
        const isHovered = hoveredItem === null || hoveredItem === itemId;

        // Check if this is a cost versus output measure
        const isCostVsOutputMeasure =
          entry.value && COST_VERSUS_OUTPUT_MEASURES.includes(entry.value as CostVersusOutputMeasures);

        return (
          <LegendItem
            key={itemId}
            value={entry.value}
            label={
              isCostVsOutputMeasure
                ? getMeasureName(entry.value as CostVersusOutputMeasures, t)
                : maybeGetNullLabelForSeries(entry.value ?? null)
            }
            color={color || ''}
            isHovered={isHovered}
            onMouseEnter={() => setHoveredItem(itemId)}
            onMouseLeave={() => setHoveredItem(null)}
          />
        );
      })}
    </LegendContainer>
  );
}

interface LegendItemProps {
  value: string;
  label: string;
  color: string;
  isHovered: boolean;
  onMouseEnter: (itemId: string) => void;
  onMouseLeave: () => void;
}

function LegendItem({ value, label, color, isHovered, onMouseEnter, onMouseLeave }: LegendItemProps) {
  return (
    <Flex
      key={value}
      align="center"
      gap={4}
      style={{
        cursor: 'pointer',
        opacity: isHovered ? 1 : 0.3,
        transition: 'opacity 0.2s ease',
      }}
      onMouseEnter={() => onMouseEnter(value)}
      onMouseLeave={onMouseLeave}
    >
      <LineChartLegendMarker color={color} />

      {label.length > 15 ? (
        <Tooltip label={label} variant="white" maw={280} multiline>
          <Text size="tiny">{label.substring(0, 15)}...</Text>
        </Tooltip>
      ) : (
        <Text size="tiny">{label}</Text>
      )}
    </Flex>
  );
}

export { Legend };
